import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getBusinessEntityDetails,
  getPendingEntityDetails,
  getTaggedMerchants,
} from "../../../services/services";
import Label from "../../../components/Label";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import { Clear } from "@mui/icons-material";
import TableView from "../../../components/Table";
import { getNextPage } from "../../../services/services";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import { linkTextStyle, styles } from "../../../utils/styles";
import { handleOpenLink } from "../../../utils/appUtils";
import { MAPPED_REGISTERED_COUNTRY, RISK_LEVEL } from "../../../utils/constants";
import moment from "moment";

const ENTITY_FIELDS = [
  { key: "business_name", label: "Business Name", uppercase: true },
  { key: "short_name", label: "Short Name" },
  { key: "uen", label: "Business ID" },
  { key: "contact_name", label: "Contact Name" },
  { key: "contact_email", label: "Contact Email" },
  { key: "contact_number", label: "Contact Number" },
  { key: "address_1", label: "Address 1" },
  { key: "address_2", label: "Address 2" },
  { key: "address_3", label: "Address 3" },
  { key: "postal_code", label: "Postal Code" },
  { key: "address_country", label: "Country", uppercase: true },
  { key: "registered_country", label: "Registered Country", type: "country" },
  { key: "gdrive_link", label: "Data Folder", type: "link" },
  { key: "data_merchant_payout", label: "Merchant Payout", type: "payout" },
  { key: "bank_account", label: "Bank Accounts", type: "bankAccounts" },
  { key: "last_kyc_review", label: "Last KYC Review", type: "date" },
  { key: "next_kyc_review", label: "Next KYC Review", type: "date" },
  { key: "risk_level", label: "Risk Level", type: "risk" },
];

const comparableEntityValue = (field, entity) => {
  if (!entity) return null;
  const value = entity[field.key];

  switch (field.type) {
    case "country":
      return value?.id ?? value?.registered_country ?? null;
    case "payout":
      return JSON.stringify(
        Array.isArray(value)
          ? [...value]
              .map(({ merchant_id, source }) => ({ merchant_id, source }))
              .sort((a, b) =>
                `${a.merchant_id}-${a.source}`.localeCompare(
                  `${b.merchant_id}-${b.source}`
                )
              )
          : []
      );
    case "bankAccounts":
      return JSON.stringify(
        Array.isArray(value)
          ? value.map((item) => item.id ?? item.account_number).sort()
          : []
      );
    default:
      return value ?? null;
  }
};

const EntityDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [entityDetails, setentityDetails] = useState();
  const [loaded, setLoaded] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const [filter, setFilter] = useState({});
  const [taggedMerchants, setTaggedMerchants] = useState();
  const [tableData, setTableData] = useState();
  const [next, setNext] = useState("");

  useEffect(() => {
    if (!detailsData && !action) {
      getTaggedMerchants(id, setTaggedMerchants, filter);
      getBusinessEntityDetails(id, setentityDetails, setLoaded);
      return;
    }
    if (!detailsData && action) {
      getPendingEntityDetails(id, setentityDetails, setLoaded);
      return;
    }

    setentityDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const formatData = (input) => {
    let tableData = [];
    input.results.forEach(
      ({
        id,
        is_active,
        is_suspended,
        mid,
        name,
        business_entity,
        registered_country,
      }) => {
        tableData.push({
          id: id,
          name: name,
          business_entity: business_entity && business_entity.business_name,
          active: is_active ? "Active" : "Inactive",
          suspended: is_suspended ? "True" : "False",
          mid: mid,
          registered_country:
            registered_country && registered_country.registered_country,
        });
      }
    );
    return tableData;
  };

  const getNext = async () => {
    if (!next) return;
    const res = await getNextPage({
      url: next,
      filter: filter,
    });
    let newRows = formatData(res);
    setTableData((old) => [...old, ...newRows]);
    setNext(res.next ? res.next : false);
  };

  useEffect(() => {
    if (taggedMerchants) {
      setTableData(formatData(taggedMerchants));
      setNext(taggedMerchants.next);
    }
  }, [taggedMerchants]);

  const handleBack = () => window.close();

  const renderEntityValue = (field, entity) => {
    const value = entity?.[field.key];
    if (value === null || value === undefined || value === "") return "N/A";

    switch (field.type) {
      case "country":
        return (
          MAPPED_REGISTERED_COUNTRY[value?.registered_country] ||
          value?.registered_country ||
          "N/A"
        );
      case "link":
        return (
          <Box
            component="span"
            sx={{ ...linkTextStyle }}
            onClick={() => value && handleOpenLink(value)}
          >
            {value}
          </Box>
        );
      case "payout":
        return Array.isArray(value) && value.length ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {value.map((item) => (
              <li
                key={`${item.merchant_id}-${item.source}`}
                style={{ ...linkTextStyle, cursor: "pointer" }}
                onClick={() =>
                  handleOpenLink(
                    `/3/app/operations/business-entity/merchant-payout/${item.merchant_id}`
                  )
                }
              >
                {item.merchant_name || item.merchant_id}{" "}
                <strong>({item.source})</strong>
              </li>
            ))}
          </Box>
        ) : (
          "N/A"
        );
      case "bankAccounts":
        return Array.isArray(value) && value.length ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {value.map((item) => (
              <li
                key={item.id || `${item.account_number}-${item.bank}`}
                style={{ ...linkTextStyle, cursor: "pointer" }}
                onClick={() =>
                  handleOpenLink(
                    `/3/app/operations/bank-account/details/${item.id}/`
                  )
                }
              >
                {item.account_number} <strong>({item.bank})</strong>
              </li>
            ))}
          </Box>
        ) : (
          "N/A"
        );
      case "date":
        return moment(value).format("DD/MM/YYYY");
      case "risk":
        return RISK_LEVEL.find((item) => item.id === value)?.label || value;
      default:
        return field.uppercase && typeof value === "string"
          ? value.toUpperCase()
          : value;
    }
  };

  const InfoColumn = ({
    entity,
    comparisonEntity,
    title,
    emptyMessage,
    greyedOut = false,
    comparisonSide,
    forceHighlightAll = false,
  }) => (
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          height: "100%",
          p: 3,
          border: "1px solid",
          borderColor: greyedOut ? "grey.400" : "#999",
          borderRadius: 2,
          bgcolor: greyedOut ? "grey.100" : "background.paper",
          color: greyedOut ? "text.disabled" : "text.primary",
          opacity: greyedOut ? 0.65 : 1,
          filter: greyedOut ? "grayscale(1)" : "none",
        }}
      >
        {title && (
          <Typography variant="h6" sx={{ mb: 3 }}>
            {title}
          </Typography>
        )}
        {!entity ? (
          <Box
            sx={{
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              border: "1px dashed",
              borderColor: "grey.300",
              borderRadius: 1,
            }}
          >
            <Typography>{emptyMessage}</Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5} alignItems="stretch">
            {ENTITY_FIELDS.map((field) => {
              const changed =
                comparisonSide &&
                (forceHighlightAll ||
                  comparableEntityValue(field, entity) !==
                    comparableEntityValue(field, comparisonEntity));
              const highlightColor =
                comparisonSide === "before" ? "#fff1f0" : "#f0fff4";
              const borderColor =
                comparisonSide === "before" ? "#ffccc7" : "#b7eb8f";

              return (
                <React.Fragment key={field.key}>
                  <Grid
                    item
                    xs={5}
                    sx={{
                      p: 1,
                      bgcolor: changed ? highlightColor : "transparent",
                      borderLeft: changed
                        ? `3px solid ${borderColor}`
                        : "3px solid transparent",
                    }}
                  >
                    <Typography variant="subtitle1">{field.label}</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={7}
                    sx={{
                      p: 1,
                      overflowWrap: "anywhere",
                      bgcolor: changed ? highlightColor : "transparent",
                      borderRight: changed
                        ? `3px solid ${borderColor}`
                        : "3px solid transparent",
                    }}
                  >
                    {renderEntityValue(field, entity)}
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        )}
      </Box>
    </Grid>
  );

  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";
    const formatTimestamp = isHistoryView ? parseDateTime : parseDate;

    return (
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Active Now"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action ? (
                <Label
                  variant="ghost"
                  color={entityDetails.is_approved ? "success" : "error"}
                >
                  {entityDetails.is_approved ? "Yes" : "No"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={entityDetails.is_active ? "success" : "error"}
                >
                  {entityDetails.is_active ? "YES" : "NO"}
                </Label>
              )}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {entityDetails.created_at
                ? formatTimestamp(entityDetails.created_at)
                : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {entityDetails.created_by
                ? entityDetails.created_by.email
                : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {entityDetails.approved_at
                ? formatTimestamp(entityDetails.approved_at)
                : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {entityDetails.approved_by
                ? entityDetails.approved_by.email
                : "N/A"}
            </Grid>

            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated on</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {entityDetails.updated_at
                    ? parseDate(entityDetails.updated_at)
                    : "N/A"}
                </Grid>

                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {entityDetails.updated_by
                    ? entityDetails.updated_by.email
                    : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  const normalizedAction = entityDetails?.action?.trim().toLowerCase();
  const comparisonActions = ["add", "update", "remove", "suspend", "unsuspend"];
  // Requests reviewed before change logs existed have no truthful "before" to show.
  // Only Update needs it: the other actions compare against the request's own copy.
  const originalUnavailable =
    normalizedAction === "update" &&
    Boolean(entityDetails?.original_unavailable);
  // action is truthy for every maker-and-checker record: the pending dialog, the
  // history dialog and the standalone history page (opened with ?action=true).
  const showComparison =
    Boolean(action) &&
    comparisonActions.includes(normalizedAction) &&
    !originalUnavailable;
  const isAddAction = normalizedAction === "add";
  const isRemoveAction = normalizedAction === "remove";
  const isSuspendAction = normalizedAction === "suspend";
  const isUnsuspendAction = normalizedAction === "unsuspend";
  const originalEntity = entityDetails?.original || null;
  const useMainDataForBefore =
    isRemoveAction || isSuspendAction || isUnsuspendAction;
  const beforeEntity = isAddAction
    ? null
    : useMainDataForBefore
    ? entityDetails
    : originalEntity;
  const afterEntity = isRemoveAction
    ? null
    : entityDetails
    ? isSuspendAction || isUnsuspendAction
      ? entityDetails
      : { ...(originalEntity || {}), ...entityDetails }
    : null;

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="Business Entity Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && entityDetails && action && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Action: {entityDetails.action}</Typography>
          {showComparison && !isRemoveAction && (
            <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    bgcolor: "#fff1f0",
                    border: "1px solid #ffccc7",
                  }}
                />
                <Typography variant="body2">Changed before value</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    bgcolor: "#f0fff4",
                    border: "1px solid #b7eb8f",
                  }}
                />
                <Typography variant="body2">Changed after value</Typography>
              </Stack>
            </Stack>
          )}
        </Box>
      )}
      {loaded && entityDetails ? (
        <Card sx={{ p: 3 }}>
          <Grid
            container
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 2, md: 2 }}
            sx={{ mt: showComparison ? 0 : 5 }}
          >
            {showComparison ? (
              <>
                <InfoColumn
                  entity={beforeEntity}
                  comparisonEntity={afterEntity}
                  title="Before"
                  emptyMessage="No existing business entity"
                  comparisonSide={isRemoveAction ? undefined : "before"}
                />
                <InfoColumn
                  entity={afterEntity}
                  comparisonEntity={beforeEntity}
                  title="After"
                  emptyMessage="Business entity will be removed"
                  greyedOut={isSuspendAction}
                  comparisonSide={isRemoveAction ? undefined : "after"}
                  forceHighlightAll={isAddAction}
                />
                <DateColumn />
              </>
            ) : (
              <>
                {action && <DateColumn />}
                <InfoColumn entity={entityDetails} />
                {!action && <DateColumn />}
              </>
            )}
          </Grid>
          {entityDetails.reject_message && (
            <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
              Reason for rejection:
              <Typography sx={{ ml: 1 }}>
                {" "}
                {entityDetails.reject_message}
              </Typography>
            </Typography>
          )}
          {entityDetails.action_message && (
            <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
              Action message:
              <Typography sx={{ ml: 1 }}>
                {" "}
                {entityDetails.action_message}
              </Typography>
            </Typography>
          )}
        </Card>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {tableData && (
        <>
          <Divider sx={{ my: 5 }} />
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            Tagged Merchants
          </Typography>
          <Card>
            <TableView
              rows={tableData}
              count={taggedMerchants.count}
              link={"/3/app/merchant-details~id~"}
            />
          </Card>
        </>
      )}
    </Container>
  );
};

export default EntityDetails;
